import React from 'react';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { ButtonSecondary, ButtonTertiary } from '@americanexpress/dls-react';
import styles from './rulePrompt.module.scss';
import { IconClose, IconSuccess } from '@americanexpress/dls-icons';
import LiteralsService from 'src/services/literals/LiteralsService';
import { DLSSize } from 'src/types/dls-size.enum';
import { useDeleteRule, useRules, useUpdateRule } from 'src/hooks/queries/rules';
import { RuleMatcher } from 'src/types/ruleMatcher.enum';
import { useDeleteUserAttribute, useUpdateUserAttributes } from 'src/hooks/queries/users';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { goToTransactionRules } from 'src/services/router.service';
import { useHistory } from 'react-router-dom';
import H from 'history';
import { UserAttributes } from 'src/types/user-attributes.enum';
import Selectors from 'src/utils/selectors';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { getLowerPriorityMatchingRule } from 'src/utils/rule.util';
import { TransactionModel } from 'src/types/transaction.model';
import { getCategoryName } from 'src/utils/category.utils';
import { useCategories } from 'src/hooks/queries/categories';

export enum RulePromptState {
    STANDARD,
    SUCCESS,
    NEVER_ASK
}

interface RulePromptWrapperProps {
    className?: string;
    transaction: TransactionModel;
    onClose: () => void;
    state: RulePromptState;
    setState: (state: RulePromptState) => void;
}

export default function RulePromptWrapper({
    className,
    transaction,
    onClose,
    state,
    setState
}: RulePromptWrapperProps): JSX.Element {
    const history = useHistory();
    const { data: rules } = useRules();
    const { data: categories } = useCategories();
    const { isMinWidthSmall } = useBreakpoints();
    const { mutateAsync: createRule } = useUpdateRule();
    const { mutateAsync: deleteRule } = useDeleteRule();
    const { mutateAsync: updateAttributes } = useUpdateUserAttributes();
    const { mutateAsync: deleteAttribute } = useDeleteUserAttribute();
    const groupName = getCategoryName(transaction.groupUid, categories);

    const handleCreateRule = async (): Promise<void> => {
        await createRule({
            matcher: RuleMatcher.CONTAINS,
            transactionName: transaction.displayName,
            groupUid: transaction.groupUid
        });
        setState(RulePromptState.SUCCESS);
    };

    const handleUndoRule = async (): Promise<void> => {
        /** If there are two rules that match the transaction, delete the one with the lower priority (this is the
         one that was just created and as of now has the lower priority, unless the user has manually changed it,
         before which the rule prompt closes and the user can't undo anymore). */
        const matchingRule = getLowerPriorityMatchingRule(transaction, rules);
        await deleteRule(matchingRule?.id);
        onClose();
    };

    const handleNeverAsk = async (): Promise<void> => {
        await updateAttributes({ [UserAttributes.NEVER_SHOW_CREATE_RULE_PROMPT]: true });
        setState(RulePromptState.NEVER_ASK);
    };

    const handleUndoNeverAsk = async (): Promise<void> => {
        await deleteAttribute(UserAttributes.NEVER_SHOW_CREATE_RULE_PROMPT);
        onClose();
    };

    return (
        <RulePrompt
            className={className}
            transactionName={transaction.displayName}
            groupName={groupName}
            onClose={onClose}
            handleCreateRule={handleCreateRule}
            handleNeverAsk={handleNeverAsk}
            handleUndoRule={handleUndoRule}
            handleUndoNeverAsk={handleUndoNeverAsk}
            history={history}
            state={state}
            isMinWidthSmall={isMinWidthSmall}
        />
    );
}

interface RulePromptProps {
    className?: string;
    transactionName: string;
    groupName: string;
    onClose: () => void;
    handleCreateRule: () => Promise<void>;
    handleNeverAsk: () => Promise<void>;
    handleUndoRule: () => Promise<void>;
    handleUndoNeverAsk: () => Promise<void>;
    history: H.History;
    state?: RulePromptState;
    isMinWidthSmall?: boolean;
}

export function RulePrompt({
    className,
    transactionName,
    groupName,
    onClose,
    handleCreateRule,
    handleNeverAsk,
    handleUndoRule,
    handleUndoNeverAsk,
    history,
    state = RulePromptState.STANDARD,
    isMinWidthSmall
}: RulePromptProps): JSX.Element {
    const closeRulePromptIcon = (
        <ButtonTertiary
            icon={<IconClose size={DLSSize.MEDIUM} />}
            data-cy={Selectors.SpendSmart.CLOSE_BUTTON}
            title="Close"
            onClick={onClose}
            className={styles.closeButton}
        />
    );

    const manageRulesLink = (
        <Anchor
            inline
            onClick={() => goToTransactionRules(false, history)}
            href={BudgetPages.TRANSACTIONS_RULES}
            data-cy={Selectors.Transactions.MANAGE_RULES}
            typographyProps={{ variant: TypographyVariants.LINK_2 }}
        >
            {LiteralsService.getLiteral('RulePrompt.manageRules')}
        </Anchor>
    );

    const undoRuleLink = (
        <Anchor
            inline
            onClick={handleUndoRule}
            typographyProps={{ variant: TypographyVariants.LINK_2 }}
            data-cy={Selectors.Transactions.UNDO_RULE}
        >
            {LiteralsService.getLiteral('RulePrompt.undo')}
        </Anchor>
    );

    const undoNeverAskLink = (
        <Anchor
            inline
            onClick={handleUndoNeverAsk}
            typographyProps={{ variant: TypographyVariants.LINK_2 }}
            data-cy={Selectors.Transactions.UNDO_NEVER_ASK}
        >
            {LiteralsService.getLiteral('RulePrompt.undo')}
        </Anchor>
    );

    const standardState = (
        <>
            <div className={styles.standardContent}>
                <div className={styles.topRow}>
                    <Typography variant={TypographyVariants.BODY} id="rule-prompt-heading">
                        {LiteralsService.getJsxLiteral('RulePrompt.heading', [transactionName, groupName])}
                    </Typography>
                    {!isMinWidthSmall && closeRulePromptIcon}
                </div>
                <div className={styles.buttons}>
                    <ButtonSecondary
                        onClick={handleCreateRule}
                        size={DLSSize.SMALL}
                        className={isMinWidthSmall ? '' : styles.largeButton}
                        data-cy={Selectors.Transactions.CREATE_RULE_BUTTON}
                    >
                        {LiteralsService.getLiteral('RulePrompt.createRule')}
                    </ButtonSecondary>
                    <ButtonTertiary
                        onClick={handleNeverAsk}
                        size={DLSSize.SMALL}
                        className={isMinWidthSmall ? '' : styles.largeButton}
                        data-cy={Selectors.Transactions.NEVER_ASK_TO_CREATE_RULE_BUTTON}
                    >
                        {LiteralsService.getLiteral('RulePrompt.neverAskToCreateRule')}
                    </ButtonTertiary>
                </div>
            </div>
            {isMinWidthSmall && closeRulePromptIcon}
        </>
    );

    const successState = (
        <>
            <IconSuccess size={DLSSize.LARGE} className={styles.successIcon} />
            <div className={styles.successContent}>
                <div className={styles.topRow}>
                    <Typography variant={TypographyVariants.BODY_BOLD} id="rule-prompt-heading">
                        {LiteralsService.getLiteral('RulePrompt.success.text1')}
                    </Typography>
                    {!isMinWidthSmall && closeRulePromptIcon}
                </div>
                <Typography variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('RulePrompt.success.text2')}
                    {manageRulesLink}.
                </Typography>
                <Typography variant={TypographyVariants.BODY_BOLD}>
                    {LiteralsService.getLiteral('RulePrompt.success.text3')}
                    {undoRuleLink}
                </Typography>
            </div>
            {isMinWidthSmall && closeRulePromptIcon}
        </>
    );

    const neverAskState = (
        <>
            <div className={styles.neverAskAgainContent}>
                <div>
                    <div className={styles.topRow}>
                        <Typography variant={TypographyVariants.BODY_BOLD} id="rule-prompt-heading">
                            {LiteralsService.getLiteral('RulePrompt.neverAskAgain.text1')}
                        </Typography>
                        {!isMinWidthSmall && closeRulePromptIcon}
                    </div>
                    <Typography variant={TypographyVariants.BODY}>
                        {LiteralsService.getLiteral('RulePrompt.neverAskAgain.text2')}
                        {manageRulesLink}.
                    </Typography>
                </div>
                <Typography variant={TypographyVariants.BODY_BOLD}>
                    {LiteralsService.getLiteral('RulePrompt.neverAskAgain.text3')}
                    {undoNeverAskLink}
                </Typography>
            </div>
            {isMinWidthSmall && closeRulePromptIcon}
        </>
    );

    return (
        <div
            className={`${styles.rulePrompt} ${className}`}
            id="rule-prompt"
            data-cy={
                state === RulePromptState.SUCCESS
                    ? Selectors.Transactions.RULE_PROMPT_SUCCESS
                    : state === RulePromptState.NEVER_ASK
                    ? Selectors.Transactions.RULE_PROMPT_NEVER_ASK
                    : Selectors.Transactions.RULE_PROMPT
            }
            aria-labelledby="rule-prompt-heading"
            aria-live="polite"
            tabIndex={0}
        >
            {state === RulePromptState.SUCCESS
                ? successState
                : state === RulePromptState.NEVER_ASK
                ? neverAskState
                : standardState}
        </div>
    );
}
